import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { SearchResult } from '#server/models';
import { useSearchContext } from '@/context';
import { trackGAEvent } from '@/services';
import { cn } from '@/utils';

interface ResultCardLinkProps extends React.ComponentProps<typeof Link> {
  result: SearchResult;
}

function ResultCardLink({ to, title, className, result, ...props }: ResultCardLinkProps) {
  const { searchQuery, facet } = useSearchContext();
  const handleSelectItem = () => {
    trackGAEvent({
      event: 'search_result_clicked',
      item_id: result.id,
      item_name: title,
      item_url: to?.toString(),
      search_term: searchQuery ?? '',
      search_facet: facet,
      search_click_count: 1,
    });
  };

  return (
    <Link
      to={to}
      target="_blank"
      onClick={handleSelectItem}
      className={cn('group focus:outline-none', className)}
      {...props}
    />
  );
}
function ResultCard({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        `flex flex-col rounded-xl border-2 border-transparent pb-4 text-card-foreground shadow-card-shadow
        group-hover:border-ring group-focus:border-focus-ring md:py-4`,
        `[&:has([data-slot=card-media])]:justify-between [&:has([data-slot=card-media])]:md:flex-row
        [&:has([data-slot=card-media])]:md:items-stretch`,
        className,
      )}
      {...props}
    />
  );
}
function ResultCardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        `@container/card-header has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 grid auto-rows-min
        grid-rows-[auto_auto] items-start gap-1.5 px-4 md:px-4`,
        className,
      )}
      {...props}
    />
  );
}
function ResultCardTag({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('mb-4', className)} {...props} />;
}
function ResultCardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-title" className={cn('mb-1 text-lg font-bold leading-7', className)} {...props} />;
}
function ResultCardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-sm leading-7 text-secondary-grey-01', className)}
      {...props}
    />
  );
}
function ResultCardWebPage({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('mb-3', className)} {...props} />;
}
function ResultCardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}
function ResultCardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('mt-4 px-4 md:px-4', className)} {...props} />;
}
function ResultCardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-footer" className={cn('[.border-t]:pt-6 flex items-center px-6', className)} {...props} />
  );
}

function ResultCardMedia({ className, ...imgProps }: React.ComponentProps<'img'>) {
  return (
    <div
      data-slot="card-media"
      className={cn('relative mr-6 w-[200px] flex-shrink-0 self-stretch overflow-hidden rounded-lg')}
    >
      <img {...imgProps} className={cn('absolute inset-0 h-full w-full object-cover', className)} />
    </div>
  );
}

export {
  ResultCard,
  ResultCardHeader,
  ResultCardFooter,
  ResultCardTitle,
  ResultCardAction,
  ResultCardDescription,
  ResultCardContent,
  ResultCardLink,
  ResultCardTag,
  ResultCardWebPage,
  ResultCardMedia,
};
